import React from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";

import type { IPublishReleaseRequest } from "@multiconsult-group/multiplacer-core";
import { usePostMultiplacerReleaseNotesPublishMutation } from "../../../openapi";


export type PublishReleaseCardProps = {
  selectedDeployNoteIds: string[];
  onPublished?: (releaseId: string) => void;
};

export const PublishReleaseCard: React.FC<PublishReleaseCardProps> = ({
  selectedDeployNoteIds,
  onPublished,
}) => {
  const [publishRelease, { isLoading, isError, error, isSuccess, data }] =
    usePostMultiplacerReleaseNotesPublishMutation();

  const form = useForm<IPublishReleaseRequest>({
    initialValues: {
      version: "",
      title: "",
      summary: "",
      deployNoteIds: [],
    },
    validate: {
      version: (v) => (v?.trim() ? null : "Påkrevd"),
      title: (v) => (v?.trim() ? null : "Påkrevd"),
    },
  });

  React.useEffect(() => {
    form.setFieldValue("deployNoteIds", selectedDeployNoteIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeployNoteIds.join("|")]);

  React.useEffect(() => {
    const releaseId = (data as any)?.releaseId as string | undefined;
    if (releaseId) onPublished?.(releaseId);
  }, [data, onPublished]);

  const handleSubmit = (values: IPublishReleaseRequest) => {
    const payload: IPublishReleaseRequest = {
      version: values.version.trim(),
      title: values?.title?.trim() ?? "",
      summary: values.summary?.trim() ? values.summary.trim() : undefined,
      deployNoteIds: selectedDeployNoteIds,
    };

    publishRelease({ body: payload } as any);
  };

  const errorMessage = isError
    ? (error as any)?.data?.message || "Det oppstod en feil under publisering."
    : undefined;

  const disabled = selectedDeployNoteIds.length === 0;

  return (
    <Card withBorder radius="lg" p="md">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Group justify="space-between" align="center">
            <Group gap="xs" align="center">
              <Title order={3}>Publiser release</Title>
              <Badge variant="light">{selectedDeployNoteIds.length} valgt</Badge>
            </Group>
          </Group>

          <Text size="sm" c="dimmed">
            Oppretter en ny release-notat og markerer valgte deploy-notater som publisert.
          </Text>

          {disabled && (
            <Alert color="gray" variant="light" title="Ingen deploy-notater valgt">
              Velg minst ett deploy-notat i listen over før du publiserer.
            </Alert>
          )}

          {errorMessage && (
            <Alert color="red" title="Kunne ikke publisere" variant="light">
              {errorMessage}
            </Alert>
          )}

          {isSuccess && (
            <Alert color="green" variant="light" title="Publisert">
              Release er publisert
              {(data as any)?.releaseId ? ` (id: ${(data as any).releaseId})` : ""}.
            </Alert>
          )}

          <Divider label="Metadata" />

          <Group grow>
            <TextInput
              label="Versjon"
              placeholder="2026.02.09"
              {...form.getInputProps("version")}
              disabled={disabled}
            />
            <TextInput
              label="Tittel"
              placeholder="Forbedret ytelse og tydeligere validering"
              {...form.getInputProps("title")}
              disabled={disabled}
            />
          </Group>

          <TextInput
            label="Oppsummering (valgfritt)"
            placeholder="Kort oppsummering som vises øverst."
            {...form.getInputProps("summary")}
            disabled={disabled}
          />

          <Group justify="flex-end">
            <Button type="submit" loading={isLoading} disabled={disabled}>
              Publiser release
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
};

export default PublishReleaseCard;
